import { Field, Form, Formik } from "formik";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import TextField from "pages/_components/fields/TextField";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
// eslint-disable-next-line import/no-named-as-default
import ResponsiveHeader, { DesktopButtons, MobileButtons } from "pages/_components/header";
import * as Yup from "yup";
import useDebounce from "pages/transfers/hooks/useDebounce";
import { actions as comexActions } from "reducers/comex";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import CustomText from "pages/_components/CustomText/CustomText";
import Image from "pages/_components/Image";
import I18n from "pages/_components/I18n";
import Checkbox from "pages/_components/Checkbox";
import DateField from "pages/_components/fields/DateField";
import Action from "pages/_components/Action";
import { push } from "connected-react-router";
import ComexStepper from "./_components/ComexStepper";

const FORM_ID = "comex.tax";
const MIN_LENGTH = 1;
const MAX_LENGTH_BASE = 20;
const MAX_LENGTH_RATE = 12;

const StepTaxRetention = ({ isDesktop }) => {
    const dispatch = useDispatch();

    const { formRef } = useDebounce();

    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const { comexTransfer: comexTransferRequest = {}, comexId = null } = useSelector((state) => state.comex);
    const { tax = {}, concept = "" } = comexTransferRequest;

    const username = sessionSelectors.getUserFullName(store.getState());
    const title = i18n.get("comex.request.title").replace("#{NAME}", username);
    const [isSaved, setIsSaved] = useState(false);

    const initialValues = {
        yesIncomeTax: tax?.yesIncomeTax ?? true,
        noIncomeTax: tax?.noIncomeTax ?? false,
        reasonIncomeTax: tax?.reasonIncomeTax ?? "",
        base: tax?.base ?? "",
        rate: tax?.rate ?? "",
        constitutionDate: tax?.constitutionDate ?? null,
        constitutionPlace: tax?.constitutionPlace ?? "",
        idBeneficiary: tax?.idBeneficiary ?? "",
        yesAgreement: tax?.yesAgreement ?? true,
        noAgreement: tax?.noAgreement ?? false,
        articleNumber: tax?.articleNumber ?? "",
        yesVat: tax?.yesVat ?? true,
        noVat: tax?.noVat ?? false,
        reasonVat: tax?.reasonVat ?? "",
        baseVat: tax?.baseVat ?? "",
        rateVat: tax?.rateVat ?? "",
        simplified: tax?.simplified ?? false,
        registered: tax?.registered ?? false,
        exempt: tax?.exempt ?? false,
    };

    const saveComexRequest = (isContinue) => {
        const _comexTransferRequest = {
            ...comexTransferRequest,
            step: "stepTAXRETENTION",
            comexId,
            isContinue,
        };
        dispatch(comexActions.createComexTransfer(_comexTransferRequest));
        setIsSaved(true);
    };

    const requiredIfYesIncomeTax = (schema) =>
        Yup.mixed().when("yesIncomeTax", {
            is: true,
            then: () => schema.required(i18n.get("global.error.required")),
            otherwise: () => schema.notRequired(),
        });

    const requiredIfYesVat = (schema, min, max) =>
        Yup.mixed().when("yesVat", {
            is: true,
            then: () =>
                schema
                    .required(i18n.get("global.error.required"))
                    .min(min, i18n.get(`${FORM_ID}.minLength.error`).replace("#{MIN}", min))
                    .max(max, i18n.get(`${FORM_ID}.maxLength.error`).replace("#{MAX}", max)),
            otherwise: () => schema.notRequired(),
        });

    const validationSchema = Yup.object().shape({
        reasonIncomeTax: requiredIfYesIncomeTax(Yup.string()),
        base: requiredIfYesIncomeTax(
            Yup.string()
                .min(MIN_LENGTH, i18n.get(`${FORM_ID}.minLength.error`).replace("#{MIN}", MIN_LENGTH))
                .max(MAX_LENGTH_BASE, i18n.get(`${FORM_ID}.maxLength.error`).replace("#{MAX}", MAX_LENGTH_BASE)),
        ),
        rate: requiredIfYesIncomeTax(
            Yup.string()
                .min(MIN_LENGTH, i18n.get(`${FORM_ID}.minLength.error`).replace("#{MIN}", MIN_LENGTH))
                .max(MAX_LENGTH_RATE, i18n.get(`${FORM_ID}.maxLength.error`).replace("#{MAX}", MAX_LENGTH_RATE)),
        ),
        constitutionDate: requiredIfYesIncomeTax(Yup.date().nullable()),
        constitutionPlace: requiredIfYesIncomeTax(Yup.string()),
        idBeneficiary: requiredIfYesIncomeTax(Yup.string()),
        articleNumber: requiredIfYesIncomeTax(
            Yup.string()
                .min(1, i18n.get(`${FORM_ID}.articleNumber.minLength.error`))
                .max(100, i18n.get(`${FORM_ID}.articleNumber.maxLength.error`)),
        ),
        baseVat: requiredIfYesVat(Yup.string(), MIN_LENGTH, MAX_LENGTH_BASE),
        rateVat: requiredIfYesVat(Yup.string(), MIN_LENGTH, MAX_LENGTH_RATE),
    });

    const handleSubmit = () => {
        saveComexRequest(true);
        const isService = concept.includes("S");
        const isGoods = concept.includes("B");

        if (isService) {
            dispatch(push("/comex/service/documentation"));
        } else if (isGoods) {
            dispatch(push("/comex/goods/documentation"));
        } else {
            dispatch(push("/comex/documentation"));
        }
    };

    const handleCheckboxChange = (setFieldValue, otherField) => {
        setFieldValue(otherField, false);
        if (otherField === "yesIncomeTax" || otherField === "noIncomeTax") {
            setFieldValue("reasonIncomeTax", "");
            setFieldValue("base", "");
            setFieldValue("rate", "");
            setFieldValue("constitutionDate", null);
            setFieldValue("constitutionPlace", "");
            setFieldValue("idBeneficiary", "");
            setFieldValue("articleNumber", "");
        }
        if (otherField === "yesVat" || otherField === "noVat") {
            setFieldValue("reasonVat", "");
            setFieldValue("baseVat", "");
            setFieldValue("rateVat", "");
        }
    };

    const handleClientConditionChange = (setFieldValue, fieldName) => {
        const checkboxes = ["registered", "exempt", "simplified"];
        checkboxes.forEach((checkbox) => {
            setFieldValue(checkbox, checkbox === fieldName);
        });
    };

    const getBackLink = () => {
        const isService = concept.includes("S");
        const isGoods = concept.includes("B");
        if (isService || (!isService && !isGoods)) {
            return "/comex/transfer";
        }
        if (isGoods) {
            return "/comex/goods/paymentdata";
        }
    };

    const [formData, setFormData] = useState({});

    useEffect(() => {
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransferRequest,
                tax: {
                    ...formData,
                },
            }),
        );

        const isClientConditionSelected = formData.registered || formData.exempt || formData.simplified;

        const isFormComplete =
            (!!formData.base || formData.noIncomeTax) &&
            (!!formData.rate || formData.noIncomeTax) &&
            (!!formData.constitutionDate || formData.noIncomeTax) &&
            (!!formData.constitutionPlace || formData.noIncomeTax) &&
            (!!formData.idBeneficiary || formData.noIncomeTax) &&
            (!!formData.reasonIncomeTax || formData.noIncomeTax) &&
            (!!formData.articleNumber || formData.noIncomeTax) &&
            (!!formData.baseVat || formData.noVat) &&
            (!!formData.rateVat || formData.noVat) &&
            (!!formData.reasonVat || !formData.noVat);

        setIsSaveEnabled(isClientConditionSelected && isFormComplete);
        setIsSaved(false);
    }, [formData, dispatch]);

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={5} />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo={getBackLink()}>
                <DesktopButtons>
                    <Button
                        className="btn btn-outline btn-block small"
                        onClick={() => {
                            saveComexRequest();
                        }}
                        disabled={!isSaveEnabled || isSaved}>
                        <Image
                            src={isSaved ? "images/figma/icon/check.svg" : "images/figma/icon/save.svg"}
                            className="icon-medium svg-icon"
                        />
                        <I18n id={isSaved ? "comex.transfer.save.ok.btn" : "comex.transfer.save.btn"} />
                    </Button>
                </DesktopButtons>
                <MobileButtons>
                    <Action
                        labelKey="comex.transfer.save.btn"
                        handleClick={() => saveComexRequest()}
                        disabled={!isSaveEnabled || isSaved}
                    />
                </MobileButtons>
            </ResponsiveHeader>

            <MainContainer showLoader={false}>
                <Notification scopeToShow="mepNotification" />
                <h2>{title}</h2>
                <I18n component="h3" id="comex.transfer.tax.retention.title" />
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={() => handleSubmit()}
                    ref={formRef}>
                    {({ values, setFieldValue, isSubmitting }) => {
                        setFormData(values);
                        const isDisabledIncomeTax = values.noIncomeTax;
                        const isDisabledVat = values.noVat;
                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={4}
                                            rowStart={1}>
                                            <CustomText
                                                text="comex.tax.retention.label"
                                                textType="I18N"
                                                className="retention-subtitle"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={4}
                                            rowStart={2}>
                                            <CustomText
                                                className="data-label"
                                                text={`${FORM_ID}.apply.label`}
                                                textType="I18N"
                                            />
                                            <div className="flex gap-12 indent-20">
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="yesIncomeTax"
                                                    labelTextProps={{ defaultValue: "yesIncomeTax" }}
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "noIncomeTax")}
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="noIncomeTax"
                                                    labelTextProps={{ defaultValue: "noIncomeTax" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "yesIncomeTax")}
                                                />
                                            </div>
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={3}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reasonIncomeTax"
                                                labelText={`${FORM_ID}.reason.label`}
                                                placeholder=" "
                                                disabled={isDisabledIncomeTax}
                                                className="indent-20"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data flexDirection="column" columnStart={2} columnWidth={4}>
                                            <CustomText
                                                className="data-label"
                                                text={`${FORM_ID}.agreement.label`}
                                                textType="I18N"
                                            />
                                            <div className="flex gap-12 indent-20">
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="yesAgreement"
                                                    labelTextProps={{ defaultValue: "yesAgreement" }}
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "noAgreement")}
                                                    disabled={isDisabledIncomeTax}
                                                />

                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="noAgreement"
                                                    labelTextProps={{ defaultValue: "noAgreement" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "yesAgreement")}
                                                    disabled={isDisabledIncomeTax}
                                                />
                                            </div>
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="articleNumber"
                                                placeholder=" "
                                                className="indent-20"
                                                disabled={isDisabledIncomeTax}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="idBeneficiary"
                                                placeholder=" "
                                                disabled={isDisabledIncomeTax}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={4} rowStart={2}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="base"
                                                placeholder=" "
                                                disabled={isDisabledIncomeTax}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={4} rowStart={3}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="rate"
                                                placeholder=" "
                                                disabled={isDisabledIncomeTax}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={4} rowStart={4}>
                                            <Field
                                                component={DateField}
                                                idForm={FORM_ID}
                                                name="constitutionDate"
                                                placeholder=" "
                                                disabled={isDisabledIncomeTax}
                                                value={values.constitutionDate || ""}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={4} rowStart={5}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="constitutionPlace"
                                                placeholder=" "
                                                disabled={isDisabledIncomeTax}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={7}>
                                            <CustomText
                                                text="comex.tax.vat.label"
                                                textType="I18N"
                                                component="h2"
                                                className="retention-subtitle"
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            flexDirection="column"
                                            columnStart={2}
                                            columnWidth={12}
                                            rowStart={8}>
                                            <CustomText
                                                className="data-label indent-20"
                                                text={`${FORM_ID}.client.condition.label`}
                                                textType="I18N"
                                            />
                                            <div
                                                className={`flex ${
                                                    isDesktop ? "flex-row gap-13 indent-20" : "flex-col gap-6 indent-40"
                                                } `}>
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="registered"
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() =>
                                                        handleClientConditionChange(setFieldValue, "registered")
                                                    }
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="exempt"
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() =>
                                                        handleClientConditionChange(setFieldValue, "exempt")
                                                    }
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="simplified"
                                                    onChange={() =>
                                                        handleClientConditionChange(setFieldValue, "simplified")
                                                    }
                                                />
                                            </div>
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={4}
                                            rowStart={9}
                                            className="retention-subtitle">
                                            <CustomText
                                                className="data-label indent-40"
                                                text={`${FORM_ID}.apply.label`}
                                                textType="I18N"
                                            />
                                            <div className="flex gap-12 indent-60">
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="yesVat"
                                                    labelTextProps={{ defaultValue: "yesVat" }}
                                                    style={{ marginRight: "16px" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "noVat")}
                                                />
                                                <Field
                                                    component={Checkbox}
                                                    idForm={FORM_ID}
                                                    name="noVat"
                                                    labelTextProps={{ defaultValue: "noVat" }}
                                                    onChange={() => handleCheckboxChange(setFieldValue, "yesVat")}
                                                />
                                            </div>
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            columnWidth={4}
                                            rowStart={10}
                                            className="retention-subtitle">
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reasonVat"
                                                labelText={`${FORM_ID}.reason.label`}
                                                placeholder=" "
                                                className="indent-60"
                                                disabled={!isDisabledVat}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={11}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="baseVat"
                                                labelText={`${FORM_ID}.base.label`}
                                                placeholder=" "
                                                disabled={isDisabledVat}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={12}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="rateVat"
                                                labelText={`${FORM_ID}.rate.label`}
                                                placeholder=" "
                                                disabled={isDisabledVat}
                                            />
                                        </GridTable.Data>
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            loading={isSubmitting}
                                            disabled={!isSaveEnabled}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

StepTaxRetention.propTypes = {
    isDesktop: bool.isRequired,
};

export default StepTaxRetention;
